import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../api"; // ✅ centralized API instance

import Sidebar from "./Sidebar";
import Header from "./Header";
import ShopFilterComponent from "./ShopFilterComponent";
import ShopCardComponent from "../components/ShopCardComponent";
import PageTitle from "./PageTitleCompoent";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";
import { initialShops } from "../components/initialShopsData";
import "../css/ShoppersAndShopScreen.css";
import PawAnimation from "./PawAnimation";

export default function ShoppersAndShopScreen() {
  const [shopList, setShopList] = useState(initialShops);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shopToDelete, setShopToDelete] = useState({ id: null, name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState({ shopName: false, shopAddress: false });
  const [inputValues, setInputValues] = useState({ shopName: "", shopAddress: "" });

  // --- Fetch Shops using centralized API ---
  const fetchShops = async () => {
    try {
      setLoading(true);
      setError("");

      const params = {};
      if (inputValues.shopName) params.name = inputValues.shopName;
      if (inputValues.shopAddress) params.address = inputValues.shopAddress;

      // ✅ Use centralized API instance
      const response = await api.get("/shops/", { params });

      setShopList(response.data);
    } catch (err) {
      console.error("Failed to fetch shops:", err);
      setError("Failed to fetch shops. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValues]);

  const handleViewProducts = (shop) => {
    navigate("/view-products", { state: { shop } });
  };

  const handleDelete = (id) => {
    setShopList((prev) => prev.filter((shop) => shop.id !== id));
  };

  const handleOpenDeleteModal = (id, name) => {
    setShopToDelete({ id, name });
    setIsModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsModalOpen(false);
    setShopToDelete({ id: null, name: "" });
  };

  const handleConfirmDelete = () => {
    if (shopToDelete && shopToDelete.id) {
      handleDelete(shopToDelete.id);
    }
    handleCloseDeleteModal();
  };

  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleInputChange = (key, value) =>
    setInputValues((prev) => ({ ...prev, [key]: value.trim() }));

  if (loading) {
    return (
      <div className="full-screen-loader">
        <PawAnimation />
      </div>
    );
  }

  return (
    <div className="shoppers-and-shop-container">
      <Sidebar />
      <div className="shoppers-and-shop-main">
        <Header />
        <div className="shoppers-and-shop-content">
          <div className="shoppers-and-shop-heading-row">
            <PageTitle title="Shoppers and Shops" />
            <div className="shoppers-and-shop-filter">
              <ShopFilterComponent
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                filters={filters}
                inputValues={inputValues}
                onFilterChange={handleFilterChange}
                onInputChange={handleInputChange}
                filterKeys={["shopName", "shopAddress"]}
              />
              <div className="shoppers-and-shop-filter-buttons">
                <motion.button
                  onClick={() => navigate("/add-shop")}
                  className="shoppers-and-shop-button"
                  aria-label="Add new shop"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24">
                    <path fill="#eba8bf" d="M19 12.998h-6v6h-2v-6H5v-2h6v-6h2v6h6z"/>
                  </svg>
                  <span className="shoppers-and-shop-button-text">Add Shop</span>
                </motion.button>
              </div>
            </div>
          </div>

          <motion.div
            className="shoppers-and-shop-grid"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
            }}
          >
            {error ? (
              <p style={{ color: "red" }}>{error}</p>
            ) : shopList.length === 0 ? (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                No shops found matching filters.
              </motion.p>
            ) : (
              <AnimatePresence>
                {shopList.map((shop) => (
                  <motion.div
                    key={shop.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 30 }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ShopCardComponent
                      shop={shop}
                      onDelete={() => handleOpenDeleteModal(shop.id, shop.shop_name)}
                      handleViewProducts={() => handleViewProducts(shop)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </motion.div>
        </div>
      </div>

      <DeleteWithConfirmation
        isOpen={isModalOpen}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={`Delete "${shopToDelete?.name}"?`}
        message="Are you sure you want to remove this shop? This action cannot be undone."
      />
    </div>
  );
}
